import {
  Badge,
  Box,
  Group,
  Menu,
  Stack,
  Table,
  ActionIcon,
  Modal,
} from "@mantine/core";
import { Link, router } from "@inertiajs/react";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import { L2KH1, L2KText } from "../../../components/ui/Typography";
import { L2KButton } from "../../../components/ui/Forms";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { PostTypeBadges } from "../../../components/posts/PostTypeBadges";

interface AdminPost {
  id: number;
  slug: string;
  title: string;
  status: "draft" | "published";
  post_type: "blog" | "podcast" | "both";
  podcast_number: number | null;
  published_at: string | null;
  created_at: string;
}

interface Props {
  posts: AdminPost[];
}

export default function AdminPostsIndex({ posts }: Props) {
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [confirmOpened, { open, close }] = useDisclosure(false);

  const doDelete = () => {
    if (confirmId == null) return;
    router.delete(`/admin/posts/${confirmId}`, { onFinish: close });
  };

  return (
    <AdminLayout>
      <Box p="lg">
        <Group justify="space-between" mb="lg">
          <L2KH1>Posts</L2KH1>
          <Link href="/admin/posts/new" style={{ textDecoration: "none" }}>
            <L2KButton leftSection={<IconPlus size={16} />}>
              New Post
            </L2KButton>
          </Link>
        </Group>

        {posts.length === 0 ? (
          <L2KText variant="secondary">No posts yet.</L2KText>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Published</Table.Th>
                <Table.Th style={{ width: 60 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {posts.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <Link
                      href={`/admin/posts/${p.slug}/edit`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      {p.title}
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <PostTypeBadges
                      postType={p.post_type}
                      podcastNumber={p.podcast_number}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={p.status === "published" ? "green" : "gray"}
                      variant="light"
                    >
                      {p.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {p.published_at
                      ? new Date(p.published_at).toLocaleDateString()
                      : "—"}
                  </Table.Td>
                  <Table.Td>
                    <Menu withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          component={Link}
                          href={`/admin/posts/${p.slug}/edit`}
                          leftSection={<IconEdit size={14} />}
                        >
                          Edit
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => {
                            setConfirmId(p.id);
                            open();
                          }}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Modal opened={confirmOpened} onClose={close} title="Delete post?">
          <Stack>
            <L2KText>This cannot be undone.</L2KText>
            <Group justify="flex-end">
              <L2KButton variant="ghost" onClick={close}>
                Cancel
              </L2KButton>
              <L2KButton color="red" onClick={doDelete}>
                Delete
              </L2KButton>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </AdminLayout>
  );
}
